import React, { useEffect, useState } from "react";
import {
  Card,
  Tabs,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Spin,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

// ==== Типы ====
interface Category {
  id: number;
  name: string;
  name_kz: string;
}

interface SubCategory {
  id: number;
  name: string;
  name_kz: string;
  category_id: number;
}

// ===== Главный компонент =====
const CategoryAdminPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  const [catLoading, setCatLoading] = useState(false);
  const [subLoading, setSubLoading] = useState(false);

  // Модалки для категории и сабкатегории
  const [catModal, setCatModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);

  const [subModal, setSubModal] = useState(false);
  const [editingSub, setEditingSub] = useState<SubCategory | null>(null);

  const [catForm] = Form.useForm();
  const [subForm] = Form.useForm();

  // === API ===

  // --- Категории ---
  const fetchCategories = async () => {
    setCatLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/categories`);
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
      // Сразу ставим активной первую категорию
      if (!activeCategoryId && data.length) setActiveCategoryId(data[0].id);
    } catch {
      message.error("Ошибка загрузки категорий");
    } finally {
      setCatLoading(false);
    }
  };

  const createOrUpdateCategory = async (values: any) => {
    try {
      let res;
      if (editingCategory) {
        res = await fetch(`${API_BASE_URL}/categories/id/${editingCategory.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(values),
        });
      } else {
        res = await fetch(`${API_BASE_URL}/categories`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(values),
        });
      }
      if (!res.ok) throw new Error();
      message.success(editingCategory ? "Категория обновлена!" : "Категория добавлена!");
      setCatModal(false);
      catForm.resetFields();
      setEditingCategory(null);
      fetchCategories();
    } catch {
      message.error("Ошибка сохранения категории");
    }
  };

  const deleteCategory = async (cat: Category) => {
    Modal.confirm({
      title: `Удалить категорию "${cat.name}"?`,
      content: "Все подкатегории этой категории также будут удалены. Продолжить?",
      okType: "danger",
      okText: "Удалить",
      cancelText: "Отмена",
      onOk: async () => {
        try {
          await fetch(`${API_BASE_URL}/categories/id/${cat.id}`, { method: "DELETE" });
          message.success("Категория удалена");
          fetchCategories();
        } catch {
          message.error("Ошибка удаления категории");
        }
      },
    });
  };

  // --- Сабкатегории ---
  const fetchSubcategories = async (category_id: number) => {
    setSubLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/categories/${category_id}/subcategories`);
      const data = await res.json();
      setSubcategories(Array.isArray(data) ? data : []);
    } catch {
      setSubcategories([]);
    } finally {
      setSubLoading(false);
    }
  };

  const createOrUpdateSub = async (values: any) => {
    try {
      let res;
      const catId = activeCategoryId;
      if (!catId) return;
      if (editingSub) {
        // PUT для обновления сабкатегории
        res = await fetch(`${API_BASE_URL}/subcategories/id/${editingSub.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...values, category_id: catId }),
        });
      } else {
        // POST для создания новой
        res = await fetch(`${API_BASE_URL}/categories/${catId}/subcategories`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(values),
        });
      }
      if (!res.ok) throw new Error();
      message.success(editingSub ? "Сабкатегория обновлена!" : "Сабкатегория добавлена!");
      setSubModal(false);
      subForm.resetFields();
      setEditingSub(null);
      fetchSubcategories(catId);
    } catch {
      message.error("Ошибка сохранения сабкатегории");
    }
  };

  const deleteSub = async (sub: SubCategory) => {
    Modal.confirm({
      title: `Удалить подкатегорию "${sub.name}"?`,
      okType: "danger",
      okText: "Удалить",
      cancelText: "Отмена",
      onOk: async () => {
        try {
          await fetch(`${API_BASE_URL}/subcategories/id/${sub.id}`, { method: "DELETE" });
          message.success("Сабкатегория удалена");
          if (activeCategoryId) fetchSubcategories(activeCategoryId);
        } catch {
          message.error("Ошибка удаления сабкатегории");
        }
      },
    });
  };

  // === EFFECTS ===

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (activeCategoryId) fetchSubcategories(activeCategoryId);
    // eslint-disable-next-line
  }, [activeCategoryId, catModal, subModal]);

  // === ТАБЫ ===

  const tabItems = categories.map((cat) => ({
    key: String(cat.id),
    label: (
      <div>
        {cat.name}
        <span style={{ color: "#888", marginLeft: 8, fontSize: 13 }}>
          / {cat.name_kz}
        </span>
      </div>
    ),
    children: (
      <Spin spinning={subLoading}>
        <Table
  dataSource={[...subcategories].sort((a, b) => a.id - b.id)}
  rowKey="id"
          size="middle"
          columns={[
            { title: "ID", dataIndex: "id", width: 50 },
            {
              title: "Название (рус)",
              dataIndex: "name",
              render: (v: string) => <b>{v}</b>,
            },
            {
              title: "Аты (қаз)",
              dataIndex: "name_kz",
              render: (v: string) => <span style={{ color: "#398" }}>{v}</span>,
            },
            {
              title: "Действия",
              key: "actions",
              width: 120,
              render: (_: any, record: SubCategory) => (
                <Space>
                  <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => {
                      setEditingSub(record);
                      setSubModal(true);
                      subForm.setFieldsValue(record);
                    }}
                  />
                  <Button
                    icon={<DeleteOutlined />}
                    danger
                    size="small"
                    onClick={() => deleteSub(record)}
                  />
                </Space>
              ),
            },
          ]}
          pagination={false}
          footer={() => (
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              block
              onClick={() => {
                setEditingSub(null);
                subForm.resetFields();
                setSubModal(true);
              }}
            >
              Добавить подкатегорию
            </Button>
          )}
        />
      </Spin>
    ),
  }));

  // ==== РЕНДЕР ====

  return (
    <Card
      title="Категории и подкатегории"
      extra={
        <Button
          icon={<PlusOutlined />}
          type="primary"
          onClick={() => {
            setEditingCategory(null);
            catForm.resetFields();
            setCatModal(true);
          }}
        >
          Добавить категорию
        </Button>
      }
    >
      <Spin spinning={catLoading}>
        <Tabs
          activeKey={activeCategoryId ? String(activeCategoryId) : undefined}
          onChange={(key) => setActiveCategoryId(Number(key))}
          items={tabItems}
        />
      </Spin>

      {/* Кнопки "Редактировать" и "Удалить" для выбранной категории */}
      {activeCategoryId && (
        <div style={{ margin: "20px 0 0 0", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              const category = categories.find((c) => c.id === activeCategoryId);
              if (category) {
                setEditingCategory(category);
                catForm.setFieldsValue(category);
                setCatModal(true);
              }
            }}
          >
            Редактировать категорию
          </Button>
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => {
              const category = categories.find((c) => c.id === activeCategoryId);
              if (category) {
                deleteCategory(category);
              }
            }}
          >
            Удалить категорию
          </Button>
        </div>
      )}

      {/* Модалка для категории */}
      <Modal
        open={catModal}
        title={editingCategory ? "Редактировать категорию" : "Добавить категорию"}
        onCancel={() => setCatModal(false)}
        onOk={() => catForm.submit()}
        okText={editingCategory ? "Сохранить" : "Создать"}
        destroyOnClose
      >
        <Form
          form={catForm}
          layout="vertical"
          onFinish={createOrUpdateCategory}
          initialValues={editingCategory || { name: "", name_kz: "" }}
        >
          <Form.Item
            label="Название (рус)"
            name="name"
            rules={[{ required: true, message: "Введите название" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Аты (қаз)"
            name="name_kz"
            rules={[{ required: true, message: "Қазақша атауын енгізіңіз" }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модалка для сабкатегории */}
      <Modal
        open={subModal}
        title={editingSub ? "Редактировать подкатегорию" : "Добавить подкатегорию"}
        onCancel={() => setSubModal(false)}
        onOk={() => subForm.submit()}
        okText={editingSub ? "Сохранить" : "Создать"}
        destroyOnClose
      >
        <Form
          form={subForm}
          layout="vertical"
          onFinish={createOrUpdateSub}
          initialValues={editingSub || { name: "", name_kz: "" }}
        >
          <Form.Item
            label="Название (рус)"
            name="name"
            rules={[{ required: true, message: "Введите название подкатегории" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Аты (қаз)"
            name="name_kz"
            rules={[{ required: true, message: "Қазақша атауын енгізіңіз" }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default CategoryAdminPage;
